import { Button, Combobox, type ComboboxItem, Group, ScrollArea, Text, useCombobox } from "@mantine/core"
import { IconCheck, IconChecks, IconX } from "@tabler/icons-react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

interface IProps {
  label: string
  value: string[] | null
  loading?: boolean
  setValue: (value: string[]) => void
  items: ComboboxItem[]
}

export function MultiFilter({ items: values = [], loading, label, value: source, setValue: setSource }: IProps) {
  const { t } = useTranslation()

  const [value, setValue] = useState<string[]>([])
  const combobox = useCombobox()

  const handleValueSelect = (val: string) =>
    setValue((current) => (current.includes(val) ? current.filter((v) => v !== val) : [...current, val]))

  useEffect(() => {
    if (source) setValue(source)
  }, [source])

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleValueSelect}
      onClose={() => setSource(value)}
      withOverlay
      withArrow
    >
      <Combobox.Target>
        <Button
          size="compact-xs"
          loading={loading}
          loaderProps={{ variant: "oval" }}
          onClick={() => {
            combobox.toggleDropdown()
            if (combobox.dropdownOpened) setSource(value)
          }}
          color={combobox.dropdownOpened ? "indigo" : value?.length ? "indigo" : "gray"}
        >
          {label}
          {source && source.length > 0 ? ` (${source.length})` : ""}
        </Button>
      </Combobox.Target>
      <Combobox.Dropdown miw={250}>
        <Combobox.Options>
          <Button
            leftSection={<IconX size={16} />}
            disabled={value.length === 0}
            color="red"
            size="compact-sm"
            variant="subtle"
            fullWidth
            onClick={() => {
              setValue([])
              setSource([])
              combobox.closeDropdown()
            }}
          >
            {t("Clear")}
          </Button>
          <ScrollArea.Autosize mah={300}>
            {values.map((item) => (
              <Combobox.Option key={item.value} value={item.value} active={value.includes(item.value)}>
                <Group gap="xs" wrap="nowrap">
                  <Text fw={value.includes(item.value) ? 700 : 500} inherit mr="auto">
                    {item.label}
                  </Text>
                  {value.includes(item.value) && <IconCheck size={16} />}
                </Group>
              </Combobox.Option>
            ))}
          </ScrollArea.Autosize>
          {value != source && (
            <Button
              leftSection={<IconChecks size={16} />}
              variant="subtle"
              size="compact-sm"
              fullWidth
              onClick={() => {
                setSource(value)
                combobox.closeDropdown()
              }}
            >
              {t("Apply")}
            </Button>
          )}
        </Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}
